import React, { useEffect, useRef } from 'react';
import './FallingTiles.css';

const COLS = 16;
const ROWS = 10;
const INITIAL_DELAY = 3000;    // ms before tiles start falling
const SPREAD_DURATION = 12000; // ms over which all tiles fall

interface RevealItem {
  src: string;
  alt: string;
  x: number;
  y: number;
}

// Things to reveal behind the tiles
const REVEAL_SOURCES: Omit<RevealItem, 'x' | 'y'>[] = [
  { src: '/assets/images/xbox.png', alt: 'Xbox' },
  { src: '/assets/images/counterstrike.png', alt: 'Counter-Strike' },
  { src: '/assets/images/clemson.png', alt: 'Clemson' },
  { src: '/assets/images/clemsonsoc.png', alt: 'Clemson SOC' },
  { src: '/assets/images/clemsonvrclub.jpg', alt: 'Clemson VR Club' },
  { src: '/assets/images/roechling.png', alt: 'Roechling' },
  { src: '/assets/images/itron.png', alt: 'Itron' },
  { src: '/assets/images/fidelity.png', alt: 'Fidelity' },
  { src: '/assets/images/claude.png', alt: 'Claude' },
  { src: '/assets/images/rl.png', alt: 'Rocket League' },
];

// Place items in a grid to avoid overlaps, kept within viewport
const REVEAL_ITEMS: RevealItem[] = (() => {
  const items = [...REVEAL_SOURCES].sort(() => Math.random() - 0.5);
  // Create grid slots in the side gutters (left 15% and right 15%)
  const slots: { x: number; y: number }[] = [];
  const cols = 2; // left gutter, right gutter
  const rows = Math.ceil(items.length / cols);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      // x: left gutter 1-10%, right gutter 72-80%
      const xBase = c === 0 ? 1 + Math.random() * 9 : 72 + Math.random() * 8;
      // y: evenly spaced with jitter, clamped to 2-90%
      const yBase = ((r + 0.5) / rows) * 86 + 2 + (Math.random() - 0.5) * 6;
      slots.push({ x: xBase, y: Math.min(Math.max(yBase, 2), 90) });
    }
  }
  return items.map((item, i) => ({
    ...item,
    x: slots[i].x,
    y: slots[i].y,
  }));
})();

const TOTAL_TILES = COLS * ROWS;

// Precompute random duration and rotation per tile (stable across renders)
const TILE_STYLES = Array.from({ length: TOTAL_TILES }, () => ({
  duration: 1.0 + Math.random() * 1.5,
  rotation: 40 + Math.random() * 50,
}));

export const FallingTiles: React.FC = () => {
  const tileRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    let mounted = true;
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Shuffled order for tile drops
    const indices = Array.from({ length: TOTAL_TILES }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    // Schedule each tile to fall via direct DOM (no re-renders)
    indices.forEach((tileIndex: number, order: number) => {
      const delay = INITIAL_DELAY + (order / TOTAL_TILES) * SPREAD_DURATION;
      const timer = setTimeout(() => {
        if (!mounted) return;
        const el = tileRefs.current[tileIndex];
        if (el) el.classList.add('falling');
      }, delay);
      timers.push(timer);
    });

    return () => {
      mounted = false;
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div className="falling-tiles-container">
      {/* Hidden content revealed as tiles fall */}
      <div className="tiles-reveal-layer">
        {REVEAL_ITEMS.map((item: RevealItem, i: number) => (
          <div
            key={i}
            className="reveal-item"
            style={{ left: `${item.x}%`, top: `${item.y}%` }}
          >
            <img src={item.src} alt={item.alt} />
          </div>
        ))}
      </div>

      {/* Tile grid overlay */}
      <div
        className="tiles-grid"
        style={{
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          gridTemplateRows: `repeat(${ROWS}, 1fr)`,
        }}
      >
        {TILE_STYLES.map((tile, i) => (
          <div
            key={i}
            ref={(el) => { tileRefs.current[i] = el; }}
            className="tile"
            style={{
              animationDuration: `${tile.duration}s`,
              '--tile-rotate': `${tile.rotation}deg`,
            } as React.CSSProperties}
          />
        ))}
      </div>
    </div>
  );
};
